import { useState } from 'react';
import { Sun, Zap, Wind, Battery, ChevronDown, Activity, Wifi, ChevronsUp, ChevronsDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Unit } from '@/types';
import { GroupedUnitList } from './GroupedUnitList';
import { useUserPermissions } from '@/hooks/useUserPermissions';

interface MobileUnitListProps {
  units: Unit[];
  totalUnits?: number; // Total count before filtering (for viewport indicator)
  selectedUnit: Unit | null;
  onSelectUnit: (unit: Unit | null, tab?: 'solar' | 'charger' | 'hvac' | 'starlink') => void;
}

type SortField = 'name' | 'battery' | 'solar' | 'charger' | 'hvac' | 'status' | 'starlink' | null;
type SortDirection = 'asc' | 'desc';

export function MobileUnitList({ units, totalUnits, selectedUnit, onSelectUnit }: MobileUnitListProps) {
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [allGroupsExpanded, setAllGroupsExpanded] = useState(true);
  const { permissions } = useUserPermissions();

  // Sort units based on selected field
  // Note: units prop is already filtered by alerts from parent
  const sortedUnits = [...units].sort((a, b) => {
    if (!sortField) return 0;

    let aValue: number;
    let bValue: number;

    switch (sortField) {
      case 'name': {
        const nameCompare = a.unit_code.localeCompare(b.unit_code);
        return sortDirection === 'asc' ? nameCompare : -nameCompare;
      }
      case 'status': {
        // Define priority order: online -> offline -> idle -> unknown
        // This means online comes first in ascending order
        const statusPriority: Record<string, number> = {
          'online': 1,
          'offline': 2,
          'idle': 3,
          'unknown': 4
        };
        
        // For 'asc' sort, we want better statuses first (lower numbers)
        // For 'desc' sort, we want worse statuses first (higher numbers)
        const aPriority = statusPriority[a.status] || 99;
        const bPriority = statusPriority[b.status] || 99;
        
        return sortDirection === 'asc'
          ? aPriority - bPriority
          : bPriority - aPriority;
      }
      case 'battery':
        aValue = a.systems?.solar?.battery ?? 0;
        bValue = b.systems?.solar?.battery ?? 0;
        break;
      case 'solar':
        aValue = a.systems?.solar?.enabled ? 1 : 0;
        bValue = b.systems?.solar?.enabled ? 1 : 0;
        break;
      case 'charger':
        aValue = a.systems?.charger?.enabled ? 1 : 0;
        bValue = b.systems?.charger?.enabled ? 1 : 0;
        break;
      case 'hvac':
        aValue = a.systems?.hvac?.enabled ? 1 : 0;
        bValue = b.systems?.hvac?.enabled ? 1 : 0;
        break;
      case 'starlink':
        // Sort by online status (Starlink connected)
        aValue = a.status === 'online' ? 1 : 0;
        bValue = b.status === 'online' ? 1 : 0;
        break;
      default:
        return 0;
    }

    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  return (
    <div className="h-full bg-background flex flex-col">
      {/* Sort Header - Updated with ALL toggle */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/20 flex-shrink-0">
        {/* Left Group: Status + Units Label */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleSort('status')}
            className={cn(
              "relative p-2 transition-colors",
              sortField === 'status' ? 'text-primary' : 'text-muted-foreground active:text-foreground'
            )}
            title="Sort by Status"
          >
            <Activity className="h-4 w-4" />
            {sortField === 'status' && (
              <ChevronDown className={cn(
                "h-2 w-2 absolute -bottom-0.5 left-1/2 -translate-x-1/2",
                sortDirection === 'asc' && 'rotate-180'
              )} />
            )}
          </button>

          <button
            onClick={() => handleSort('name')}
            className="text-xs font-medium text-muted-foreground uppercase tracking-wider active:text-foreground transition-colors"
            title="Sort by Name"
          >
            {totalUnits && units.length < totalUnits ? (
              <><span className="text-[#8FB560]">{units.length}</span>/{totalUnits} UNITS</>
            ) : (
              <>{units.length} UNITS</>
            )}
            {sortField === 'name' && (
              <ChevronDown className={cn(
                "h-2 w-2 inline-block ml-1",
                sortDirection === 'asc' && "rotate-180"
              )} />
            )}
          </button>
        </div>

        {/* Center: Expand/Collapse ALL Toggle */}
        <button
          onClick={() => setAllGroupsExpanded(prev => !prev)}
          className="flex items-center gap-1 text-[#8FB560] active:opacity-70 transition-opacity p-2"
          title={allGroupsExpanded ? "Collapse all groups" : "Expand all groups"}
        >
          {allGroupsExpanded ? (
            <ChevronsUp className="h-4 w-4" />
          ) : (
            <ChevronsDown className="h-4 w-4" />
          )}
          <span className="text-[9px] font-semibold uppercase tracking-tight leading-none">ALL</span>
        </button>

        {/* Right Group: Battery + System Icons */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleSort('battery')}
            className={cn(
              "relative p-2 transition-colors",
              sortField === 'battery' ? 'text-primary' : 'text-muted-foreground active:text-foreground'
            )}
            title="Sort by Battery"
          >
            <Battery className="h-4 w-4" />
            {sortField === 'battery' && (
              <ChevronDown className={cn(
                "h-2 w-2 absolute -bottom-0.5 left-1/2 -translate-x-1/2",
                sortDirection === 'asc' && 'rotate-180'
              )} />
            )}
          </button>

          <button
            onClick={() => handleSort('solar')}
            className={cn(
              "relative p-2 transition-colors",
              sortField === 'solar' ? 'text-primary' : 'text-muted-foreground active:text-foreground'
            )}
            title="Sort by Solar"
          >
            <Sun className="h-4 w-4" />
            {sortField === 'solar' && (
              <ChevronDown className={cn(
                "h-2 w-2 absolute -bottom-0.5 left-1/2 -translate-x-1/2",
                sortDirection === 'asc' && 'rotate-180'
              )} />
            )}
          </button>

          <button
            onClick={() => handleSort('charger')}
            className={cn(
              "relative p-2 transition-colors",
              sortField === 'charger' ? 'text-primary' : 'text-muted-foreground active:text-foreground'
            )}
            title="Sort by Charger"
          >
            <Zap className="h-4 w-4" />
            {sortField === 'charger' && (
              <ChevronDown className={cn(
                "h-2 w-2 absolute -bottom-0.5 left-1/2 -translate-x-1/2",
                sortDirection === 'asc' && 'rotate-180'
              )} />
            )}
          </button>

          <button
            onClick={() => handleSort('hvac')}
            className={cn(
              "relative p-2 transition-colors",
              sortField === 'hvac' ? 'text-primary' : 'text-muted-foreground active:text-foreground'
            )}
            title="Sort by HVAC"
          >
            <Wind className="h-4 w-4" />
            {sortField === 'hvac' && (
              <ChevronDown className={cn(
                "h-2 w-2 absolute -bottom-0.5 left-1/2 -translate-x-1/2",
                sortDirection === 'asc' && 'rotate-180'
              )} />
            )}
          </button>

          <button
            onClick={() => handleSort('starlink')}
            className={cn(
              "relative p-2 transition-colors",
              sortField === 'starlink' ? 'text-primary' : 'text-muted-foreground active:text-foreground'
            )}
            title="Sort by Starlink"
          >
            <Wifi className="h-4 w-4" />
            {sortField === 'starlink' && (
              <ChevronDown className={cn(
                "h-2 w-2 absolute -bottom-0.5 left-1/2 -translate-x-1/2",
                sortDirection === 'asc' && 'rotate-180'
              )} />
            )}
          </button>
        </div>
      </div>

      {/* Grouped Unit List */}
      <GroupedUnitList
        units={sortedUnits}
        permissions={permissions}
        selectedUnit={selectedUnit}
        onUnitClick={(unit) => onSelectUnit(unit)}
        sortBy={sortField || 'name'}
        sortOrder={sortDirection}
        allExpanded={allGroupsExpanded}
        onToggleAll={() => setAllGroupsExpanded(prev => !prev)}
        isMobile={true}
      />
    </div>
  );
}
